import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  Heart, Trash2, Package, Search, Play, 
  Edit2, Bell, BellOff, MapPin, Tag, DollarSign, Filter,
  Clock, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import { DashboardContainer, PageHeader, FilterChips } from '../components/DashboardLayout';
import { useAuth, useLanguage, API } from '../App';

const FavoritesPage = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('favorites');
  const [favorites, setFavorites] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchesLoading, setSearchesLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchFavorites();
      fetchSavedSearches();
    }
  }, [user]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedSearches = async () => {
    try {
      const response = await axios.get(`${API}/saved-searches`);
      setSavedSearches(response.data.saved_searches || []);
    } catch (error) {
      // Silent fail - will show empty list
    } finally {
      setSearchesLoading(false);
    }
  };

  const removeFavorite = async (listingId) => {
    try {
      await axios.delete(`${API}/favorites/${listingId}`);
      setFavorites(favorites.filter(f => f.listing_id !== listingId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  const clearAllFavorites = async () => {
    if (!window.confirm('Are you sure you want to clear all favorites?')) return;
    
    try {
      await axios.delete(`${API}/favorites`);
      setFavorites([]);
      toast.success('All favorites cleared');
    } catch (error) {
      toast.error('Failed to clear favorites');
    }
  };

  const deleteSavedSearch = async (searchId) => {
    if (!window.confirm('Delete this saved search?')) return;
    
    try {
      await axios.delete(`${API}/saved-searches/${searchId}`);
      setSavedSearches(savedSearches.filter(s => s.id !== searchId));
      toast.success('Saved search deleted');
    } catch (error) {
      toast.error('Failed to delete saved search');
    }
  };

  const runSavedSearch = (search) => {
    // Build query params from saved search - include ALL saved filters
    const params = new URLSearchParams();
    
    // Search query
    if (search.query) params.set('q', search.query);
    
    // Basic filters
    if (search.filters?.category) params.set('category', search.filters.category);
    if (search.filters?.subcategory) params.set('subcategory', search.filters.subcategory);
    if (search.filters?.listing_type) params.set('type', search.filters.listing_type);
    if (search.filters?.location) params.set('location', search.filters.location);
    if (search.filters?.island) params.set('island', search.filters.island);
    
    // Price filters
    if (search.filters?.min_price) params.set('min', search.filters.min_price);
    if (search.filters?.max_price) params.set('max', search.filters.max_price);
    
    // Sort
    if (search.filters?.sort && search.filters.sort !== 'newest') {
      params.set('sort', search.filters.sort);
    }
    
    // Location-based filters
    if (search.filters?.radius) params.set('radius', search.filters.radius);
    if (search.filters?.lat) params.set('lat', search.filters.lat);
    if (search.filters?.lon) params.set('lon', search.filters.lon);
    
    // Dynamic property filters (props_*)
    if (search.filters) {
      Object.entries(search.filters).forEach(([key, value]) => {
        if (key.startsWith('props_') && value) {
          params.set(key, value);
        }
      });
    }
    
    // Add saved search indicator
    params.set('savedSearch', search.name);
    params.set('savedSearchId', search.id);
    
    // Clear new_results count
    clearNewResults(search.id);
    
    navigate(`/marketplace?${params.toString()}`);
  };

  const clearNewResults = async (searchId) => {
    try {
      await axios.put(`${API}/saved-searches/${searchId}/clear-new`);
      setSavedSearches(savedSearches.map(s => 
        s.id === searchId ? { ...s, new_results: 0 } : s
      ));
    } catch (error) {
      console.error('Error clearing new results:', error);
    }
  };

  const formatFilters = (filters) => {
    if (!filters) return [];
    const parts = [];
    
    // Category
    if (filters.category) {
      parts.push({ 
        icon: Tag, 
        text: filters.category.replace(/-/g, ' '),
        color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
      });
    }
    
    // Subcategory
    if (filters.subcategory && !filters.subcategory.startsWith('all-')) {
      parts.push({ 
        icon: Tag, 
        text: filters.subcategory,
        color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
      });
    }
    
    // Location
    if (filters.location) {
      parts.push({ 
        icon: MapPin, 
        text: filters.location,
        color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
      });
    }
    
    // Island
    if (filters.island) {
      const islandName = filters.island === 'curacao' ? '🇨🇼 Curaçao' : 
                         filters.island === 'aruba' ? '🇦🇼 Aruba' : 
                         filters.island === 'bonaire' ? '🇧🇶 Bonaire' : filters.island;
      parts.push({ 
        icon: MapPin, 
        text: islandName,
        color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400'
      });
    }
    
    // Listing type
    if (filters.listing_type) {
      const typeText = filters.listing_type === 'sale' ? 'For Sale' : 
                       filters.listing_type === 'rent' ? 'For Rent' : filters.listing_type;
      parts.push({ 
        icon: Filter, 
        text: typeText,
        color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
      });
    }
    
    // Price range
    if (filters.min_price || filters.max_price) {
      const priceText = filters.min_price && filters.max_price 
        ? `$${filters.min_price} - $${filters.max_price}`
        : filters.min_price ? `$${filters.min_price}+` : `Up to $${filters.max_price}`;
      parts.push({ 
        icon: DollarSign, 
        text: priceText,
        color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
      });
    }
    
    // Sort order
    if (filters.sort && filters.sort !== 'newest') {
      const sortText = filters.sort === 'price_low' ? 'Price: Low→High' : 
                       filters.sort === 'price_high' ? 'Price: High→Low' : 
                       filters.sort === 'popular' ? 'Most Popular' : filters.sort;
      parts.push({ 
        icon: Filter, 
        text: sortText,
        color: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
      });
    }
    
    // Dynamic property filters (props_*)
    Object.entries(filters).forEach(([key, value]) => {
      if (key.startsWith('props_') && value) {
        const propName = key.replace('props_', '').replace(/_/g, ' ');
        parts.push({ 
          icon: Tag, 
          text: `${propName}: ${value}`,
          color: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
        });
      }
    });
    
    return parts;
  };

  const totalNewMatches = savedSearches.reduce((sum, s) => sum + (s.new_results || 0), 0);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <DashboardContainer>
          <div className="py-16 text-center">
            <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-4">Sign in to view favorites</h1>
            <p className="text-muted-foreground mb-6">Save your favorite listings and searches</p>
            <Link to="/auth" className="btn-primary inline-block">
              Sign In
            </Link>
          </div>
        </DashboardContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <DashboardContainer>
        {/* Standardized Header */}
        <PageHeader pageKey="favorites" />

        {/* Filter Chips */}
        <FilterChips
          options={[
            { key: 'favorites', label: 'Favorites', icon: Heart, count: loading ? undefined : favorites.length },
            { key: 'searches', label: 'Saved Searches', icon: Search, count: loading ? undefined : savedSearches.length }
          ]}
          activeFilter={activeTab}
          onFilterChange={setActiveTab}
        />

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <>
            {/* Actions */}
            {favorites.length > 0 && (
              <div className="flex justify-end mb-4">
                <button 
                  onClick={clearAllFavorites}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="card p-4 animate-pulse">
                    <div className="aspect-[4/3] bg-muted rounded-xl mb-4"></div>
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && favorites.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
                  <Heart className="w-12 h-12 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-semibold mb-2">No favorites yet</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Start exploring and save listings you love by clicking the heart icon
                </p>
                <Link to="/marketplace" className="btn-primary inline-flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Browse Marketplace
                </Link>
              </div>
            )}

            {/* Favorites Grid */}
            {!loading && favorites.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {favorites.map((favorite) => (
                  <div key={favorite.listing_id} className="relative group">
                    <ListingCard listing={favorite.listing} />
                    <button
                      onClick={() => removeFavorite(favorite.listing_id)}
                      className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                      title="Remove from favorites"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Saved Searches Tab */}
        {activeTab === 'searches' && (
          <>
            {/* Loading State */}
            {searchesLoading && (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="card p-4 animate-pulse">
                    <div className="h-5 bg-muted rounded w-1/3 mb-3"></div>
                    <div className="h-4 bg-muted rounded w-2/3 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/4"></div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!searchesLoading && savedSearches.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
                  <Search className="w-12 h-12 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-semibold mb-2">No saved searches</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Save your searches on the marketplace to get notified when new matching listings are posted
                </p>
                <Link to="/marketplace" className="btn-primary inline-flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Start Searching
                </Link>
              </div>
            )}

            {/* Saved Searches List */}
            {!searchesLoading && savedSearches.length > 0 && (
              <div className="grid gap-4">
                {savedSearches.map((search) => {
                  const filters = formatFilters(search.filters);
                  const hasNewResults = search.new_results > 0;
                  
                  return (
                    <div 
                      key={search.id} 
                      className={`p-5 sm:p-6 rounded-2xl border-2 transition-all hover:shadow-lg bg-card ${
                        hasNewResults ? 'border-green-500/50 bg-green-500/5' : 'border-border'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        {/* Search Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3">
                            <div className={`p-2.5 rounded-xl flex-shrink-0 ${
                              hasNewResults ? 'bg-green-500/20' : 'bg-primary/10'
                            }`}>
                              <Search className={`w-5 h-5 ${hasNewResults ? 'text-green-500' : 'text-primary'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-lg truncate">{search.name}</h3>
                                {hasNewResults && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full animate-pulse">
                                    <Sparkles className="w-3 h-3" />
                                    {search.new_results} new match{search.new_results > 1 ? 'es' : ''}
                                  </span>
                                )}
                              </div>
                              
                              {search.query && (
                                <p className="text-muted-foreground mt-1">
                                  Search: "{search.query}"
                                </p>
                              )}
                              
                              {/* Filters */}
                              {filters.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {filters.map((filter, idx) => (
                                    <span 
                                      key={idx}
                                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${filter.color || 'bg-secondary text-foreground'}`}
                                    >
                                      <filter.icon className="w-3.5 h-3.5" />
                                      <span className="capitalize">{filter.text}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* Created date */}
                              <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
                                <Clock className="w-3.5 h-3.5" />
                                Saved {new Date(search.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                          <button
                            onClick={() => runSavedSearch(search)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                              hasNewResults 
                                ? 'bg-green-500 hover:bg-green-600 text-white' 
                                : 'bg-primary hover:bg-primary/90 text-white'
                            }`}
                          >
                            <Play className="w-4 h-4" />
                            {hasNewResults ? 'View New' : 'Run Search'}
                          </button>
                          <button
                            onClick={() => deleteSavedSearch(search.id)}
                            className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete saved search"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Info */}
            {!searchesLoading && savedSearches.length > 0 && (
              <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Get notified of new matches</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      When new listings match your saved searches, you'll receive a notification automatically.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </DashboardContainer>

      <Footer />
    </div>
  );
};

export default FavoritesPage;
